"use client";

import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import type { EventParams } from "@/utils/analytics";
import { trackEvent } from "@/utils/analytics";
import { getOrCreateHomeEntryAttribution } from "@/utils/attribution";
import { cn } from "@/utils/cn";
import { getOrCreateHomeSessionId } from "@/utils/homeSession";
import { getOrCreateTrialSessionId } from "@/utils/trialTracking";
import { getTrialBaseUrl } from "@/utils/trialUrl";

import { CompetencyStep } from "./trial-quickstart/CompetencyStep";
import { ConditionStep } from "./trial-quickstart/ConditionStep";
import { HomeWowPreview } from "./trial-quickstart/HomeWowPreview";
import type { LevelId, PositionId, Selection, StageId, WizardStep } from "./trial-quickstart/model";
import { freshSelection, getPosition, summarizeSelection, synthesizeJd } from "./trial-quickstart/model";
import { PositionStep } from "./trial-quickstart/PositionStep";
import { StepDots } from "./trial-quickstart/StepDots";
import {
  selectPosition,
  toggleCompetencySelection,
  toggleDomainSelection,
  toggleExcludeSelection,
  toggleRequiredSelection,
} from "./trial-quickstart/selection";

const TRIAL_EXTRACT_PATH = "/extract";

type TrialHeroVariantProps = {
  experimentParams: EventParams;
};

export default function TrialHeroVariant({ experimentParams }: TrialHeroVariantProps) {
  const path = usePathname() ?? "/";
  const [step, setStep] = useState<WizardStep>(1);
  const [selection, setSelection] = useState<Selection>(() => freshSelection("backend"));
  const [submitting, setSubmitting] = useState(false);
  const viewedRef = useRef(false);
  const engagedRef = useRef(false);

  const attributionContext = useMemo(() => getOrCreateHomeEntryAttribution(), []);
  const position = getPosition(selection.positionId);
  const canProceed = step !== 2 || selection.competencyIds.length > 0;

  function trackHome(eventName: string, params?: EventParams) {
    trackEvent(eventName, path, {
      ...attributionContext,
      ...experimentParams,
      onboarding_version: "v2_quickstart",
      surface: "home",
      ...params,
    });
  }

  function markEngaged() {
    if (engagedRef.current) return;
    engagedRef.current = true;
    trackHome("quickstart_opened", {
      source: "inline",
      step,
      position: selection.positionId,
      level: selection.level,
    });
  }

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackHome("quickstart_viewed", { step: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickPosition(id: PositionId) {
    markEngaged();
    const previousPosition = selection.positionId;
    const changed = previousPosition !== id;
    setSelection((current) => selectPosition(current, id));
    trackHome("quickstart_position_selected", {
      position: id,
      previous_position: previousPosition,
      changed,
      level: selection.level,
      step: 1,
    });
  }

  function pickLevel(id: LevelId) {
    markEngaged();
    const previousLevel = selection.level;
    setSelection((current) => ({ ...current, level: id }));
    trackHome("quickstart_level_selected", {
      level: id,
      previous_level: previousLevel,
      changed: previousLevel !== id,
      position: selection.positionId,
      step: 1,
    });
  }

  function toggleCompetency(id: string) {
    const result = toggleCompetencySelection(selection, id);

    setSelection(result.nextSelection);
    trackHome("quickstart_competency_toggled", {
      position: selection.positionId,
      competency: id,
      selected: result.selected,
      selected_count_after: result.selectedCountAfter,
      required_count_after: result.requiredCountAfter,
      step: 2,
    });
  }

  function toggleRequired(id: string) {
    const result = toggleRequiredSelection(selection, id);
    if (!result) return;

    setSelection(result.nextSelection);
    trackHome("quickstart_required_toggled", {
      position: selection.positionId,
      competency: id,
      required: result.required,
      selected_count: result.selectedCount,
      required_count_after: result.requiredCountAfter,
      step: 2,
    });
  }

  function setStage(id: StageId) {
    const previousStage = selection.stage;
    setSelection((current) => ({ ...current, stage: id }));
    trackHome("quickstart_stage_selected", {
      stage: id,
      previous_stage: previousStage,
      changed: previousStage !== id,
      position: selection.positionId,
      level: selection.level,
      step: 3,
    });
  }

  function toggleDomain(id: string) {
    const result = toggleDomainSelection(selection, id);
    if (!result) return;

    setSelection(result.nextSelection);
    trackHome("quickstart_domain_toggled", {
      domain: id,
      selected: result.selected,
      domain_count_after: result.domainCountAfter,
      position: selection.positionId,
      level: selection.level,
      step: 3,
    });
  }

  function toggleExclude(id: string) {
    const result = toggleExcludeSelection(selection, id);

    setSelection(result.nextSelection);
    trackHome("quickstart_exclude_toggled", {
      exclude: id,
      selected: result.selected,
      exclude_count_after: result.excludeCountAfter,
      position: selection.positionId,
      level: selection.level,
      step: 3,
    });
  }

  function goTo(next: WizardStep) {
    markEngaged();
    trackHome("quickstart_step_changed", {
      from_step: step,
      to_step: next,
      direction: next > step ? "next" : "prev",
      position: selection.positionId,
      level: selection.level,
    });
    setStep(next);
  }

  function finishQuickstart() {
    if (submitting) return;
    setSubmitting(true);
    const jd = synthesizeJd(selection);
    const trialSessionId = getOrCreateTrialSessionId();
    const homeSessionId = getOrCreateHomeSessionId();
    const params = new URLSearchParams({
      utm_source: "homepage",
      utm_medium: "organic",
      utm_campaign: "hero-quickstart",
      utm_content: "trial",
      quickstart: "1",
      sid: trialSessionId,
      hid: homeSessionId,
      experimentId: String(experimentParams.experiment_id || ""),
      variant: String(experimentParams.variant || ""),
      position: selection.positionId,
      level: selection.level,
      competencies: selection.competencyIds.join(","),
      required: selection.requiredIds.join(","),
      stage: selection.stage,
      domains: selection.domainIds.join(","),
      excludes: selection.excludeIds.join(","),
    });
    const destination = `${getTrialBaseUrl()}${TRIAL_EXTRACT_PATH}?${params.toString()}`;
    const summary = summarizeSelection(selection);

    trackHome("quickstart_completed", summary);
    trackHome("jd_submitted", {
      method: "quickstart",
      position: selection.positionId,
      level: selection.level,
      length: jd.length,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
    });
    trackHome("trial_redirect_started", {
      method: "quickstart",
      position: selection.positionId,
      level: selection.level,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
      destination_host: new URL(destination).host,
      destination_path: TRIAL_EXTRACT_PATH,
    });

    window.location.href = destination;
  }

  return (
    <section className="trial-surface flex h-auto min-h-[620px] w-full flex-col items-center justify-center px-side py-6 desktop:min-h-[calc(100vh-4rem)] desktop:py-4">
      <div className="w-full max-w-5xl">
        <div className="mb-5 text-center tablet:mb-6">
          <div className="mb-2.5 flex justify-center tablet:mb-3">
            <span className="trial-glass-pill inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--trial-brand)]">
              <span className="trial-brand-gradient inline-flex h-4 w-4 items-center justify-center rounded-full text-white">
                ⌕
              </span>
              데이터로 검증하는 AI 리크루터
            </span>
          </div>
          <h1 className="text-[32px] font-bold leading-[1.08] tracking-[-0.025em] text-[var(--trial-foreground)] tablet:text-[44px]">
            공고로는 놓치던 인재를
            <br />
            <span className="trial-brand-gradient-text">RAG 기반 AI</span>가 발굴하고
            <br />
            데이터로 검증합니다
          </h1>
        </div>

        <div className="grid items-start gap-8 desktop:grid-cols-2 desktop:gap-12">
          <div>
            <div className="trial-glass-strong rounded-[28px] p-5 sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(79,109,245,0.1)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--trial-brand)]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--trial-brand)]" />
                  빠른 시작 · 약 30초
                </span>
                <StepDots step={step} />
              </div>

              {step === 1 ? <PositionStep selection={selection} onPickPosition={pickPosition} onPickLevel={pickLevel} /> : null}
              {step === 2 ? (
                <CompetencyStep
                  position={position}
                  selection={selection}
                  onToggle={toggleCompetency}
                  onToggleRequired={toggleRequired}
                />
              ) : null}
              {step === 3 ? (
                <ConditionStep
                  selection={selection}
                  onStage={setStage}
                  onToggleDomain={toggleDomain}
                  onToggleExclude={toggleExclude}
                />
              ) : null}

              <div className="mt-5 flex items-center justify-between gap-3 border-t border-[rgba(15,23,42,0.08)] pt-4">
                <button
                  type="button"
                  onClick={() => goTo((step - 1) as WizardStep)}
                  className={cn(
                    "px-2 py-2 text-[13.5px] text-[var(--trial-muted-strong)] transition-colors hover:text-[var(--trial-foreground)]",
                    step === 1 && "invisible",
                  )}
                >
                  ← 이전
                </button>
                {step < 3 ? (
                  <button
                    type="button"
                    onClick={() => goTo((step + 1) as WizardStep)}
                    disabled={!canProceed}
                    className="trial-brand-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_8px_24px_-6px_rgba(79,109,245,0.5)] transition-all enabled:hover:scale-[1.02] enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    다음 →
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={finishQuickstart}
                    disabled={submitting}
                    className="trial-brand-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_8px_24px_-6px_rgba(79,109,245,0.5)] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                  >
                    {submitting ? "이동 중..." : "3명 후보 발굴 시작 →"}
                  </button>
                )}
              </div>
            </div>

            <div className="mt-2 flex items-center justify-center gap-1.5 text-[12px] text-[var(--trial-muted)]">
              <span className="text-[var(--trial-success)]">✓</span>
              가입 없이 · 무료
            </div>
          </div>

          <HomeWowPreview />
        </div>
      </div>
    </section>
  );
}
